import { useEffect, useState } from "react";
import FloatingLogo3D from "../components/FloatingLogo3D";
import { useNavigate } from "react-router-dom";
import { ScanLine } from "lucide-react";

export default function Home() {
  const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
  const [accounts, setAccounts] = useState([]);
  const [groups,   setGroups]   = useState([]);
  const [engine,   setEngine]   = useState(() => {
    return localStorage.getItem("ct_engine") === "running";
  });

  const [showEnginePanel, setShowEnginePanel] = useState(false);
  const [showStatsPanel, setShowStatsPanel] = useState(false);
  const [showFiiDiiPanel, setShowFiiDiiPanel] = useState(false);
  const [fiiDii, setFiiDii] = useState(null);
  const [fiiDiiLoading, setFiiDiiLoading] = useState(false);
  const [fiiDiiError, setFiiDiiError] = useState("");
  const [bseFiiDii, setBseFiiDii] = useState(null);
  const [bseFiiDiiError, setBseFiiDiiError] = useState("");
  const navigate = useNavigate();

  // ── Load from localStorage ────────────────────────────────
  const loadData = () => {
    try {
      const acc = JSON.parse(localStorage.getItem("ct_accounts")) || [];
      const grp = JSON.parse(localStorage.getItem("ct_groups"))   || [];
      setAccounts(acc);
      setGroups(grp);
    } catch {
      setAccounts([]);
      setGroups([]);
    }
  };

  // Initial load + poll every 2s (picks up changes from Accounts/Groups pages)
  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 2000);
    return () => clearInterval(interval);
  }, []);

    useEffect(() => {
    const loadEngineStatus = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/copy/engine/status`);
        const data = await res.json().catch(() => ({}));

        if (res.ok && data.success) {
          setEngine(data.running === true);
          localStorage.setItem("ct_engine", data.running ? "running" : "stopped");
        }
      } catch (err) {
        console.log("Engine status load failed:", err.message);
      }
    };

    loadEngineStatus();
  }, [API_BASE]);

    // FII DII Data
    
    const loadFiiDii = async () => {
      try {
        setFiiDiiLoading(true);
        setFiiDiiError("");

        const res = await fetch(`${API_BASE}/api/market/fii-dii`);
        const data = await res.json();

        if (!res.ok || !data.success) throw new Error(data.error || "Failed to fetch FII-DII");

        setFiiDii(data);
      } catch (err) {
        setFiiDiiError(err.message);
      } finally {
        setFiiDiiLoading(false);
      }
    };

    const loadBseFiiDii = async () => {
      try {
        setBseFiiDiiError("");

        const res = await fetch(
          `${API_BASE}/api/market/fii-dii-bse?t=${Date.now()}`,
          { cache: "no-store" }
        );

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data.success) {
          throw new Error(data.error || "BSE FII-DII data failed");
        }

        setBseFiiDii(data);
      } catch (err) {
        setBseFiiDiiError(
          err.message || "BSE FII-DII data failed"
        );
      }
    };

    useEffect(() => {
      const loadAllFiiDii = () => {
        loadFiiDii();
        loadBseFiiDii();
      };

      loadAllFiiDii();

      const timer = setInterval(
        loadAllFiiDii,
        10 * 60 * 1000
      );

      return () => clearInterval(timer);
    }, [API_BASE]);

  // ── Sync groups to backend ───────────────────────────────
  const syncGroupsToBackend = async () => {
    try {
      const ctGroups   = JSON.parse(localStorage.getItem("ct_groups")   || "[]");
      const ctAccounts = JSON.parse(localStorage.getItem("ct_accounts") || "[]");
      const res = await fetch("/api/copy/groups/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ctGroups, ctAccounts }),
      });
      const data = await res.json();
      console.log(`✅ Synced ${data.groupCount} group(s) to backend copy engine`);
    } catch (e) {
      console.log("⚠ Group sync failed:", e.message);
    }
  };

    useEffect(() => {
    if (engine) {
      syncGroupsToBackend();
    }
  }, [engine]);

  // ── Engine toggle ─────────────────────────────────────────
    const toggleEngine = async () => {
    const next = !engine;

    try {
      if (next) {
        await syncGroupsToBackend();
      }

      const res = await fetch(`${API_BASE}/api/copy/engine/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ running: next }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Engine update failed");
      }

      setEngine(data.running === true);
      localStorage.setItem("ct_engine", data.running ? "running" : "stopped");

      console.log(data.running ? "Copy Trading Engine started" : "Copy Trading Engine stopped");
    } catch (err) {
      console.log("Engine toggle failed:", err.message);
    }
  };

  // ── Derived stats ─────────────────────────────────────────
  const totalAccounts    = accounts.length;
  const connectedAccounts = accounts.filter(a => a.status === "Connected").length;
  const totalCopies      = accounts.reduce((sum, a) => sum + (a.copied || 0), 0);
  const successRate      = totalCopies > 0 ? ((totalCopies / totalCopies) * 100).toFixed(1) : "0.0";
  const totalGroups      = groups.length;
  const activeGroups     = groups.filter(g => g.active).length;
  const totalParents     = groups.length;
  const totalChildren    = groups.reduce((sum, g) => sum + (g.children?.length || 0), 0);

  const formatCr = value =>
  `${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} Cr`;

  const netColor = value => Number(value || 0) >= 0 ? "#22c55e" : "#ef4444";

  const getFiiDiiTotals = data => {
  const fiiBuy = Number(data?.fii?.buy || 0);
  const fiiSell = Number(data?.fii?.sell || 0);
  const diiBuy = Number(data?.dii?.buy || 0);
  const diiSell = Number(data?.dii?.sell || 0);

  const totalBuy = fiiBuy + diiBuy;
  const totalSell = fiiSell + diiSell;
  const net = totalBuy - totalSell;

  return {
    totalBuy,
    totalSell,
    net,
    signal: totalBuy > totalSell ? "Strong Buy" : totalSell > totalBuy ? "Strong Sell" : "Neutral",
    color: totalBuy > totalSell ? "#22c55e" : totalSell > totalBuy ? "#ef4444" : "#facc15",
  };
};

  const stats = [
    {
      label: "Copy Groups",
      value: totalGroups,
      sub: `${activeGroups} Active`,
      subColor: "#22c55e",
      iconBg: "#3b82f6",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="22" height="22">
          <rect x="2" y="3" width="20" height="5" rx="1"/><rect x="2" y="10" width="20" height="5" rx="1"/><rect x="2" y="17" width="20" height="5" rx="1"/>
        </svg>
      ),
    },
    {
      label: "Total Accounts",
      value: totalAccounts,
      sub: `${connectedAccounts} Connected`,
      subColor: "#22c55e",
      iconBg: "#16a34a",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="22" height="22">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
    },
    {
      label: "Today's Copies",
      value: totalCopies,
      sub: `Success Rate: ${successRate}%`,
      subColor: "#22c55e",
      iconBg: "#7c3aed",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="22" height="22">
          <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
        </svg>
      ),
    },
    {
      label: "Parent / Child",
      value: `${totalParents}/${totalChildren}`,
      sub: `${totalChildren} child accounts`,
      subColor: "#94a3b8",
      iconBg: "#ea580c",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="22" height="22">
          <circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><path d="M8 13H5a2 2 0 0 0-2 2v2h4"/><path d="M16 13h3a2 2 0 0 1 2 2v2h-4"/><circle cx="5"  cy="19" r="2"/><circle cx="19" cy="19" r="2"/><path d="M12 11v8"/><circle cx="12" cy="21" r="2"/>
        </svg>
      ),
    },
  ];

  return (
    <div className="p-6 text-white">
      <div className="flex items-center justify-between gap-4 mb-6">
        <h1 className="text-3xl font-bold">
          Welcome to Nexovate Groups - "Think Big, Give time"
        </h1>

        <button
          type="button"
          className="btn-solid-green rounded-lg px-4 py-3 flex items-center gap-2"
          onClick={() => navigate("/mobile-scanner")}
          title="Scan desktop login"
        >
          <ScanLine size={20} />
          <span className="hidden sm:inline">Scan Login</span>
        </button>
      </div>

      <FloatingLogo3D />

      {/* ── Copy Trading Engine Banner ─────────────────────── */}
      <div className="rounded-xl mt-6 mb-4 overflow-hidden"
  style={{ background: "linear-gradient(135deg, #1e293b, #0f172a)", border: "1px solid #334155" }}>
  <button
    type="button"
    onClick={() => setShowEnginePanel(prev => !prev)}
    className="w-full px-6 py-4 flex items-center justify-between text-left"
  >
    <div>
      <h2 className="text-xl font-bold">Copy Trading Engine</h2>
      <p className="text-sm text-gray-400">
        Status:{" "}
        <span style={{ color: engine ? "#22c55e" : "#ef4444", fontWeight: "bold" }}>
          {engine ? "Running" : "Stopped"}
        </span>
      </p>
    </div>

    <span className="text-cyan-300 text-sm font-semibold">
      {showEnginePanel ? "Hide" : "Show"}
    </span>
  </button>

  {showEnginePanel && (
    <div className="px-6 pb-6 flex justify-between items-center">
      <div>
        <p className="text-base">
          Status:{" "}
          <span style={{ color: engine ? "#22c55e" : "#ef4444", fontWeight: "bold" }}>
            {engine ? "Running" : "Stopped"}
          </span>
        </p>

        {engine && (
          <p className="text-sm text-gray-400 mt-1">
            Monitoring {activeGroups} group{activeGroups !== 1 ? "s" : ""} · {connectedAccounts} account{connectedAccounts !== 1 ? "s" : ""} connected
          </p>
        )}
      </div>

      <button
        onClick={toggleEngine}
        style={{
          background: engine
            ? "linear-gradient(135deg, #b91c1c, #dc2626)"
            : "linear-gradient(135deg, #15803d, #22c55e)",
          border: "none",
          color: "#fff",
          padding: "14px 32px",
          borderRadius: 10,
          cursor: "pointer",
          fontSize: 16,
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          gap: 10,
          boxShadow: engine ? "0 0 20px #dc262650" : "0 0 20px #22c55e50",
          transition: "all 0.2s",
        }}
      >
        {engine ? "Stop Copy Trading" : "Start Copy Trading"}
      </button>
    </div>
  )}
</div>

      <div className="rounded-xl mb-4 overflow-hidden"
  style={{ background: "#111827", border: "1px solid #334155" }}>
  <button
    type="button"
    onClick={() => setShowFiiDiiPanel(prev => !prev)}
    className="w-full px-6 py-4 flex items-center justify-between text-left"
  >
    <div>
      <h2 className="text-xl font-bold">FII-DII Activity</h2>
      <p className="text-sm text-gray-400">
        {fiiDii?.date ? `Latest: ${fiiDii.date} · ${fiiDii.bias}` : "Latest institutional cash activity"}
      </p>
    </div>

    <span className="text-cyan-300 text-sm font-semibold">
      {showFiiDiiPanel ? "Hide" : "Show"}
    </span>
  </button>

  {showFiiDiiPanel && (
    <div className="px-6 pb-6">
      <button
        type="button"
        onClick={() => {
          loadFiiDii();
          loadBseFiiDii();
        }}
        className="mb-4 px-4 py-2 rounded bg-blue-500 font-semibold"
      >
        Refresh
      </button>

      {fiiDiiLoading && <div className="text-slate-400">Fetching FII-DII data...</div>}
      {fiiDiiError && <div className="text-red-300">{fiiDiiError}</div>}

      {fiiDii && (
        <div className="grid grid-cols-2 gap-4">
          {(() => {
            const totals = getFiiDiiTotals(fiiDii);

            return (
              <div className="rounded-xl p-4 mb-4" style={{ background: "#0f172a", border: "1px solid #334155" }}>
                <div className="text-lg font-bold mb-2">FII + DII Totals</div>

                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-slate-400">Total Buy</div>
                    <div className="text-green-300 font-bold">₹{formatCr(totals.totalBuy)}</div>
                  </div>

                  <div>
                    <div className="text-slate-400">Total Sell</div>
                    <div className="text-red-300 font-bold">₹{formatCr(totals.totalSell)}</div>
                  </div>

                  <div>
                    <div className="text-slate-400">Signal</div>
                    <div className="font-bold" style={{ color: totals.color }}>
                      {totals.signal}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {[
            { label: "FII / FPI", data: fiiDii.fii },
            { label: "DII", data: fiiDii.dii },
          ].map(item => (
            <div
              key={item.label}
              className="rounded-xl p-4"
              style={{
                background: "#0f172a",
                border: "1px solid #334155",
              }}
            >
              <div className="text-lg font-bold mb-3">
                {item.label}
              </div>

              <div className="text-sm text-slate-400">
                Buy: ₹{formatCr(item.data.buy)}
              </div>

              <div className="text-sm text-slate-400">
                Sell: ₹{formatCr(item.data.sell)}
              </div>

              <div
                className="text-2xl font-bold mt-2"
                style={{ color: netColor(item.data.net) }}
              >
                Net: ₹{formatCr(item.data.net)}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 border-t border-yellow-500/30 pt-5">
        <div className="mb-3">
          <div className="text-lg font-bold text-yellow-300">
            BSE FII-DII Activity
          </div>

          <div className="text-sm text-gray-400">
            {bseFiiDii?.date
              ? `Latest: ${bseFiiDii.date} · ${bseFiiDii.bias}`
              : "Latest BSE institutional cash activity"}
          </div>
        </div>

        {bseFiiDiiError && (
          <div className="mb-3 text-red-300">
            {bseFiiDiiError}
          </div>
        )}

        {bseFiiDii && (
          <div className="grid gap-4 md:grid-cols-2">
            {[
              { label: "FII / FPI", data: bseFiiDii.fii },
              { label: "DII", data: bseFiiDii.dii },
            ].map(item => (
              <div
                key={`bse-${item.label}`}
                className="rounded-xl p-4"
                style={{
                  background: "#0f172a",
                  border: "1px solid #334155",
                }}
              >
                <div className="mb-3 text-lg font-bold">
                  {item.label}
                </div>

                <div className="text-sm text-slate-400">
                  Buy: ₹{formatCr(item.data?.buy)}
                </div>

                <div className="text-sm text-slate-400">
                  Sell: ₹{formatCr(item.data?.sell)}
                </div>

                <div
                  className="mt-2 text-2xl font-bold"
                  style={{ color: netColor(item.data?.net) }}
                >
                  Net: ₹{formatCr(item.data?.net)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )}
</div>

      {/* ── Stat Cards ────────────────────────────────────── */}
      <div className="rounded-xl mb-4 overflow-hidden"
  style={{ background: "#1e293b", border: "1px solid #334155" }}>
  <button
    type="button"
    onClick={() => setShowStatsPanel(prev => !prev)}
    className="w-full px-6 py-4 flex items-center justify-between text-left"
  >
    <div>
      <h2 className="text-xl font-bold">Copy Groups & Total Accounts</h2>
      <p className="text-sm text-gray-400">
        {totalGroups} groups · {totalAccounts} accounts · {totalChildren} child accounts
      </p>
    </div>

    <span className="text-cyan-300 text-sm font-semibold">
      {showStatsPanel ? "Hide" : "Show"}
    </span>
  </button>

  {showStatsPanel && (
    <div className="grid grid-cols-4 gap-6 px-6 pb-6">
      {stats.map((s) => (
        <div
          key={s.label}
          className="rounded-xl p-6 flex justify-between items-start"
          style={{ background: "#0f172a", border: "1px solid #334155" }}
        >
          <div>
            <p className="text-gray-400 text-sm mb-2">{s.label}</p>
            <p className="text-4xl font-bold mb-3">{s.value}</p>
            <p className="text-sm" style={{ color: s.subColor }}>{s.sub}</p>
          </div>

          <div style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            background: s.iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: `0 0 16px ${s.iconBg}60`,
          }}>
            {s.icon}
          </div>
        </div>
      ))}
    </div>
  )}
</div>


      {/* ── Quick status when engine is running ───────────── */}
      {engine && (
        <div className="mt-6 rounded-xl p-4"
          style={{ background: "#0f2040", border: "1px solid #1e3a5f" }}>
          <p className="text-xs text-gray-400 uppercase tracking-widest mb-3">Live Engine Status</p>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Active Groups",      value: activeGroups,      color: "#22c55e" },
              { label: "Connected Accounts", value: connectedAccounts, color: "#38bdf8" },
              { label: "Copies Today",       value: totalCopies,       color: "#a78bfa" },
            ].map(item => (
              <div key={item.label} className="text-center">
                <div style={{ fontSize: 28, fontWeight: "bold", color: item.color }}>{item.value}</div>
                <div style={{ fontSize: 11, color: "#64748b", letterSpacing: 1 }}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}